import { Pinia, Store } from '../pinia-class-component';
import { Entity } from '@/models/types/Entity';
import { entityTypes, Status } from '@/enums/generic';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { useFormStore } from '@/store/form/formStore';
import { WebEvents } from '@/native/plugins/WebEvents';
import { useIdle, UseIdleReturn } from '@vueuse/core';

export function useLayoutStore() {
  return new LayoutStore();
}

class LoaderStatus {
  text?: string;
  status?: Status;

  constructor(text = '', status = Status.PENDING) {
    this.text = text;
    this.status = status;
  }
}

export enum WorkMode {
  ORDER = 'ORDER',
  LIST = 'LIST',
  DEEP_LINK = 'DEEP_LINK',
  SANDBOX = 'SANDBOX',
  DEV_MODE = 'DEV_MODE',
}

export enum Tabs {
  DEVICES = 'Geräte',
  PLANTS = 'Anlagen',
  RADIATORS = 'Heizkörper',
}

export const workModeConfig: Record<WorkMode, any> = {
  LIST: {
    enableLocalStorage: false,
    enableSaving: true,
    showSaveButton: true,
  },
  ORDER: {
    enableLocalStorage: true,
    enableSaving: true,
    showSaveButton: true,
  },
  DEEP_LINK: {
    enableLocalStorage: true,
    enableSaving: true,
    showSaveButton: true,
  },
  SANDBOX: {
    enableLocalStorage: true,
    enableSaving: false,
    showSaveButton: false,
  },
  DEV_MODE: {
    enableLocalStorage: true,
    enableSaving: false,
    showSaveButton: false,
  },
};

@Store
export class LayoutStore extends Pinia {
  private _isUserMenuOpen: boolean;
  private _isDarkMode: boolean;
  private _appVersionName: string;
  private _currentRoute: string;
  private _loader: any;
  private _workMode: WorkMode;
  private _selectedTab: Tabs;

  constructor() {
    super();
    this._isOnline = true;
    this._isSideMenuOpen = false;
    this._selectedTab = Tabs.DEVICES;
    this._isFormDialogOpen = false;
    this._isOrderFormOpen = false;
    this._isCancelFormPromptOpen = false;
    this._isPhotoPromptOpen = false;
    this._isUserMenuOpen = false;
    this._isLoading = false;
    this._loader = new LoaderStatus();
    this._currentNode = undefined;
    this._isDarkMode = false;
    this._appVersionName = '';
    this._currentRoute = '';
    this._workMode = WorkMode.ORDER;
    this._isUserIdle = useIdle(1000 * 5);
  }

  private _isOnline: boolean;

  get isOnline(): boolean {
    return this._isOnline;
  }

  private _isSideMenuOpen: boolean;

  get isSideMenuOpen(): boolean {
    return this._isSideMenuOpen;
  }

  private _isFormDialogOpen: boolean;
  private _isOrderFormOpen: boolean;
  private _isCancelFormPromptOpen: boolean;
  private _isPhotoPromptOpen: boolean;
  private _isUserIdle: UseIdleReturn;

  get isUserIdle() {
    return this._isUserIdle.idle;
  }

  resetIsUserIdle() {
    this._isUserIdle.reset();
  }

  get selectedTab(): string {
    return this._selectedTab;
  }

  selectTab(tab: Tabs) {
    this._selectedTab = tab;
  }

  get isFormDialogOpen(): boolean {
    return this._isFormDialogOpen;
  }

  get isOrderFormOpen(): boolean {
    return this._isOrderFormOpen;
  }

  setCancelPromptState(state: boolean) {
    this._isCancelFormPromptOpen = state;
  }

  get isCancelFormPromptOpen(): boolean {
    return this._isCancelFormPromptOpen;
  }

  setPhotoPromptState(state: boolean) {
    this._isPhotoPromptOpen = state;
  }

  get isPhotoPromptOpen(): boolean {
    return this._isPhotoPromptOpen;
  }

  private _isLoading: boolean;

  get isLoading(): boolean {
    return this._isLoading;
  }

  get loader(): boolean {
    return this._loader;
  }

  private _currentNode: Entity | undefined;

  get currentNode(): Entity | undefined {
    return this._currentNode;
  }

  get pageContext() {
    return this._currentRoute;
  }

  changeCurrentNode(node: Entity) {
    this._currentNode = node;

    if (node.type === entityTypes.building && node.id) {
      const buildingStore = useBuildingStore();
      buildingStore.setCurrentBuilding(node.id);
    }
  }

  deactivateDarkMode() {
    this._isDarkMode = false;
    document.documentElement.classList.remove('dark');
    window.localStorage.setItem('isDarkMode', 'false');
    WebEvents.onThemeToggle({ isDarkMode: false });
  }

  activateDarkMode() {
    this._isDarkMode = true;
    document.documentElement.classList.add('dark');
    window.localStorage.setItem('isDarkMode', 'true');
    WebEvents.onThemeToggle({ isDarkMode: true });
  }

  disableDarkModeTemporarily() {
    document.documentElement.classList.remove('dark');
  }

  isDarkMode(): boolean {
    return this._isDarkMode;
  }

  toggleUserMenu() {
    this._isUserMenuOpen = !this._isUserMenuOpen;
  }

  openSideMenu() {
    this._isSideMenuOpen = true;
  }

  openFormDialog() {
    this._isFormDialogOpen = true;
    this._isCancelFormPromptOpen = false;
  }

  closeSideMenu() {
    this._isSideMenuOpen = false;
  }

  closeFormDialog() {
    this._isFormDialogOpen = false;
    this._isCancelFormPromptOpen = false;
    useFormStore().resetCurrentForm();
  }

  setOrderFormState(state: boolean) {
    this._isOrderFormOpen = state;
    this._isCancelFormPromptOpen = false;
  }

  toggleSideMenu() {
    this._isSideMenuOpen = !this._isSideMenuOpen;
  }

  setOnlineState(state: boolean) {
    this._isOnline = state;
  }

  setLoadingState(state: boolean) {
    this._loader = { text: '', status: Status.PENDING };
    this._isLoading = state;
  }

  resolveLoaderState(text: string, isSuccessful: boolean) {
    const status = isSuccessful ? Status.SUCCESS : Status.ERROR;
    this._loader = new LoaderStatus(text, status);

    setTimeout(() => {
      this._loader = new LoaderStatus();
    }, 2000);
  }

  getAppVersionName() {
    return this._appVersionName;
  }

  setRoute(route: string) {
    this._currentRoute = route;
  }

  goBack() {
    if (this._isFormDialogOpen || this._isOrderFormOpen) {
      if (this._isCancelFormPromptOpen) {
        this.closeFormDialog();
        this.setOrderFormState(false);
        return;
      }

      if (useFormStore().hasFormChanges) {
        this.setCancelPromptState(true);
        return;
      }

      this.closeFormDialog();
      return;
    }

    if (this._currentRoute === 'Home') {
      return;
    }

    window.history.back();
  }

  get workMode() {
    return this._workMode;
  }

  get isSandboxMode() {
    return this._workMode === WorkMode.SANDBOX;
  }

  setWorkMode(workMode: WorkMode) {
    this._workMode = workMode;
  }

  isLocalStorageEnabled(): boolean {
    if (!this._workMode || this._workMode === WorkMode.ORDER) return true;
    return workModeConfig[this._workMode].enableLocalStorage;
  }

  isSavingEnabled() {
    if (!this._workMode || this._workMode === WorkMode.ORDER) return true;
    return workModeConfig[this._workMode].enableSaving;
  }
}
